import React, { useRef, useEffect, useState } from 'react';
import { Button, SafeArea, Dialog } from 'antd-mobile';
import { useCalculator } from '../hooks/useCalculator';
import { useTransactions } from '../hooks/useTransactions';
import { useStore } from '../store';
import { formatCurrency } from '@tangtung/shared';
import { useNavigate } from 'react-router-dom';
import './CalculatorPage.css';

export default function CalculatorPage() {
    const { expression, result, hasResult, inputDigit, inputOperator, deleteDigit, calculate, clear } = useCalculator();
    const { saveTransaction } = useTransactions();
    const user = useStore((s) => s.user);
    const navigate = useNavigate();
    const expressionRef = useRef<HTMLDivElement>(null);
    const [savingCash, setSavingCash] = useState(false);

    // Auto-scroll expression to the right when it changes
    useEffect(() => {
        if (expressionRef.current) {
            expressionRef.current.scrollLeft = expressionRef.current.scrollWidth;
        }
    }, [expression]);

    const handleCreateQR = () => {
        if (result > 0) navigate('/qr');
    };

    const handleCashPayment = async () => {
        if (result <= 0) return;

        setSavingCash(true);
        const txResult = await saveTransaction('cash', result);
        setSavingCash(false);

        if (txResult) {
            // Send receipt flex message to LINE (async, don't wait)
            if (user?.userId) {
                fetch('https://tangtungdev-api.greenslime2009.workers.dev/api/receipts/send', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        line_user_id: user.userId,
                        amount: result,
                        payment_type: 'cash',
                        transaction_id: txResult.id,
                    }),
                }).catch(console.error);
            }

            // Show success and go back home
            navigate('/');
        }
    };

    const handleFrequentNumbers = () => {
        navigate('/pos');
    };

    // Format result with thousand separators
    const formatResult = (num: number) => {
        return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <div className="calculator-page">
            <SafeArea position="top" />

            {/* Display - Rolling History Style */}
            <div className="calc-display">
                {/* Expression (top) - scrollable, shows all input */}
                <div className="calc-expression" ref={expressionRef}>
                    {expression || '0'}
                </div>
                {/* Result (bottom) - large, shows after pressing = */}
                <div className="calc-result">
                    {hasResult && `= ${formatResult(result)}`}
                </div>
            </div>

            {/* Keypad - iOS Layout (5 rows × 4 cols) */}
            <div className="calc-keypad">
                {/* Row 1: ⌫, AC, %, ÷ */}
                <Button className="calc-key operator" onClick={() => deleteDigit()}>⌫</Button>
                <Button className="calc-key operator" onClick={() => clear()}>AC</Button>
                <Button className="calc-key operator" onClick={() => inputOperator('%')}>%</Button>
                <Button className="calc-key operator" onClick={() => inputOperator('/')}>÷</Button>

                {/* Row 2: 7, 8, 9, × */}
                <Button className="calc-key" onClick={() => inputDigit('7')}>7</Button>
                <Button className="calc-key" onClick={() => inputDigit('8')}>8</Button>
                <Button className="calc-key" onClick={() => inputDigit('9')}>9</Button>
                <Button className="calc-key operator" onClick={() => inputOperator('*')}>×</Button>

                {/* Row 3: 4, 5, 6, − */}
                <Button className="calc-key" onClick={() => inputDigit('4')}>4</Button>
                <Button className="calc-key" onClick={() => inputDigit('5')}>5</Button>
                <Button className="calc-key" onClick={() => inputDigit('6')}>6</Button>
                <Button className="calc-key operator" onClick={() => inputOperator('-')}>−</Button>

                {/* Row 4: 1, 2, 3, + */}
                <Button className="calc-key" onClick={() => inputDigit('1')}>1</Button>
                <Button className="calc-key" onClick={() => inputDigit('2')}>2</Button>
                <Button className="calc-key" onClick={() => inputDigit('3')}>3</Button>
                <Button className="calc-key operator" onClick={() => inputOperator('+')}>+</Button>

                {/* Row 5: grid, 0, ., = */}
                <Button className="calc-key minipos" onClick={handleFrequentNumbers}>
                    <div className="minipos-label">
                        <span>Mini</span>
                        <strong>POS</strong>
                    </div>
                </Button>
                <Button className="calc-key" onClick={() => inputDigit('0')}>0</Button>
                <Button className="calc-key" onClick={() => inputDigit('.')}>.</Button>
                <Button className="calc-key operator" onClick={() => calculate()}>=</Button>
            </div>

            {/* Actions - Cash and QR buttons */}
            <div className="calc-actions two-buttons">
                <Button
                    className="calc-btn cash"
                    onClick={handleCashPayment}
                    disabled={result <= 0}
                    loading={savingCash}
                >
                    💵 เงินสด
                </Button>
                <Button
                    className="calc-btn primary"
                    onClick={handleCreateQR}
                    disabled={result <= 0}
                >
                    📱 สร้าง QR
                </Button>
            </div>

            <SafeArea position="bottom" />
        </div>
    );
}
